import { Component, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Option } from '@digilize/shared/definitions/src';
import { TranslateService } from '@ngx-translate/core';
import { Education } from '../../models/education.model';
import { EducationMockService } from '../../services/education-mock.service';

@Component({
  selector: 'bwc-education-search',
  templateUrl: './education-search.component.html',
  styleUrls: ['./education-search.component.scss'],
})
export class EducationSearchComponent implements OnInit {
  educations: Education[] = [];
  visibleCount = 24;
  likedIds: number[] = [];
  searchWat = '';
  searchWaar = '';

  selectedCategorie = '';
  selectedRegio = '';
  selectedLeervorm = '';
  selectedPrijs = '';
  selectedAanbieder = '';
  selectedSubsidie = '';
  selectedStartDatum = '';
  selectedTaal = '';

  categorieControl = new FormControl('');
  regioControl = new FormControl('');
  leervormControl = new FormControl('');
  prijsControl = new FormControl('');
  aanbiederControl = new FormControl('');
  subsidieControl = new FormControl('');
  startDatumControl = new FormControl('');
  taalControl = new FormControl('');

  categorieOptions: Option[] = [];
  regioOptions: Option[] = [];
  leervormOptions: Option[] = [];
  prijsOptions: Option[] = [];
  aanbiederOptions: Option[] = [];
  subsidieOptions: Option[] = [];
  startDatumOptions: Option[] = [];
  taalOptions: Option[] = [];

  constructor(
    private educationService: EducationMockService,
    private route: ActivatedRoute,
    private router: Router,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      this.searchWat = params['wat'] || '';
      this.searchWaar = params['waar'] || '';
    });
    this.educationService.searchEducations(this.searchWat).subscribe(e => (this.educations = e));
    this.likedIds = this.educationService.getSavedIds();

    this.categorieOptions = [
      { text: this.translateService.instant('educations.search.filter_business'), val: 'bedrijfsbeheer' },
      { text: this.translateService.instant('educations.search.filter_finance'), val: 'financien' },
      { text: this.translateService.instant('educations.search.filter_marketing_sales'), val: 'marketing' },
      { text: this.translateService.instant('educations.search.filter_it'), val: 'it' },
      { text: this.translateService.instant('educations.search.filter_languages_comm'), val: 'talen' },
      { text: this.translateService.instant('educations.search.filter_hr'), val: 'hr' },
      { text: this.translateService.instant('educations.search.filter_legal'), val: 'juridisch' },
      { text: this.translateService.instant('educations.search.filter_personal_dev'), val: 'persoonlijk' },
    ];

    this.regioOptions = [
      { text: 'Online', val: 'online' },
      { text: 'Antwerpen', val: 'antwerpen' },
      { text: 'Brussel', val: 'brussel' },
      { text: 'Limburg', val: 'limburg' },
      { text: 'Oost-Vlaanderen', val: 'oost-vlaanderen' },
      { text: 'Vlaams-Brabant', val: 'vlaams-brabant' },
      { text: 'West-Vlaanderen', val: 'west-vlaanderen' },
    ];

    this.leervormOptions = [
      { text: this.translateService.instant('educations.search.filter_distance_learning'), val: 'afstandsleren' },
      { text: this.translateService.instant('educations.search.filter_classroom'), val: 'klassikaal' },
      { text: 'Online', val: 'online' },
      { text: 'Blended learning', val: 'blended' },
      { text: this.translateService.instant('educations.search.filter_evening'), val: 'avondonderwijs' },
    ];

    this.prijsOptions = [
      { text: this.translateService.instant('educations.search.filter_free'), val: 'gratis' },
      { text: '€0 - €500', val: '0-500' },
      { text: '€500 - €1.000', val: '500-1000' },
      { text: '€1.000 - €2.500', val: '1000-2500' },
      { text: '€2.500+', val: '2500+' },
    ];

    this.aanbiederOptions = [
      { text: 'LOI', val: 'loi' },
      { text: 'NHA Afstandsonderwijs', val: 'nha' },
      { text: 'NCOI', val: 'ncoi' },
      { text: 'Syntra', val: 'syntra' },
      { text: 'VDAB', val: 'vdab' },
      { text: "CVO's", val: 'cvo' },
    ];

    this.subsidieOptions = [
      { text: this.translateService.instant('educations.search.filter_kmo_portefeuille'), val: 'kmo-portefeuille' },
      { text: this.translateService.instant('educations.search.filter_vdab_opleiding'), val: 'vdab' },
      { text: this.translateService.instant('educations.search.filter_opleidingscheques'), val: 'opleidingscheques' },
      { text: this.translateService.instant('educations.search.filter_no_subsidy'), val: 'geen' },
    ];

    this.startDatumOptions = [
      { text: this.translateService.instant('educations.search.filter_start_now'), val: 'direct' },
      { text: this.translateService.instant('educations.search.filter_this_month'), val: 'deze-maand' },
      { text: this.translateService.instant('educations.search.filter_next_month'), val: 'volgende-maand' },
      { text: this.translateService.instant('educations.search.filter_this_quarter'), val: 'dit-kwartaal' },
      { text: this.translateService.instant('educations.search.filter_flexible'), val: 'flexibel' },
    ];

    this.taalOptions = [
      { text: this.translateService.instant('educations.search.filter_dutch'), val: 'nederlands' },
      { text: this.translateService.instant('educations.search.filter_english'), val: 'engels' },
      { text: this.translateService.instant('educations.search.filter_french'), val: 'frans' },
      { text: this.translateService.instant('educations.search.filter_german'), val: 'duits' },
    ];
  }

  get visibleEducations(): Education[] {
    return this.educations.slice(0, this.visibleCount);
  }

  get hasMore(): boolean {
    return this.visibleCount < this.educations.length;
  }

  loadMore(): void {
    this.visibleCount = Math.min(this.visibleCount + 24, this.educations.length);
  }

  handleSearch(): void {
    const params: Record<string, string> = {};
    if (this.searchWat) params['wat'] = this.searchWat;
    if (this.searchWaar) params['waar'] = this.searchWaar;
    this.router.navigate(['/profile-check/educations/search'], { queryParams: params });
    this.educationService.searchEducations(this.searchWat).subscribe(e => (this.educations = e));
    this.visibleCount = 24;
  }

  clearWat(): void {
    this.searchWat = '';
  }

  clearWaar(): void {
    this.searchWaar = '';
  }

  isLiked(id: number): boolean {
    return this.likedIds.includes(id);
  }

  toggleLike(education: Education): void {
    const saved = this.educationService.toggleSaveEducation(education);
    this.likedIds = saved
      ? [...this.likedIds, education.id]
      : this.likedIds.filter(i => i !== education.id);
  }

  onCategorieSelected(option: Option): void {
    this.selectedCategorie = option.val;
  }

  onRegioSelected(option: Option): void {
    this.selectedRegio = option.val;
  }

  onLeervormSelected(option: Option): void {
    this.selectedLeervorm = option.val;
  }

  onPrijsSelected(option: Option): void {
    this.selectedPrijs = option.val;
  }

  onAanbiederSelected(option: Option): void {
    this.selectedAanbieder = option.val;
  }

  onSubsidieSelected(option: Option): void {
    this.selectedSubsidie = option.val;
  }

  onStartDatumSelected(option: Option): void {
    this.selectedStartDatum = option.val;
  }

  onTaalSelected(option: Option): void {
    this.selectedTaal = option.val;
  }
}
